import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * Two input treatments exist in `Visual - Demo.dc.html`:
 *
 *   default  white, the signature 1.5px border, 11px radius — every search
 *            field, filter and form field in the app
 *   hero     the Site Brain ask field, which sits on dark chrome: no border
 *            at all, a 13px radius and a hard drop shadow instead, padded to
 *            clear a leading search icon and the trailing 46px mic button
 *
 * The design draws exactly one focus state in the whole file (the
 * modify-decision textarea darkens its border to the ink colour), so that is
 * what we ship — layered on top of the global `:focus-visible` outline, which
 * the design omits but we are not shipping without.
 */
const inputVariants = cva(
  [
    "w-full font-sans text-ink placeholder:text-faint",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        default: [
          "rounded-input border-[1.5px] border-border bg-surface",
          "px-[14px] py-3 text-[0.9rem]",
          "focus-visible:border-ink",
        ],
        hero: [
          "rounded-card border-none bg-surface shadow-input",
          "py-[17px] pr-16 pl-[50px] text-[1.04rem] font-medium",
        ],
      },
      /**
       * Reserve room for a leading icon. The design absolutely positions the
       * icon at `left:14px` and pads the field to 42px; `hero` already bakes
       * its own 50px lead-in for the icon at `left:18px`.
       */
      withIcon: { true: "", false: "" },
    },
    compoundVariants: [
      { variant: "default", withIcon: true, className: "pl-[42px]" },
    ],
    defaultVariants: { variant: "default", withIcon: false },
  },
);

type InputProps = ComponentProps<"input"> & VariantProps<typeof inputVariants>;

export function Input({ className, variant, withIcon, ...props }: InputProps) {
  return (
    <input
      data-slot="input"
      className={cn(inputVariants({ variant, withIcon }), className)}
      {...props}
    />
  );
}

export { inputVariants };
export type { InputProps };
